import {
  enableProdMode,
  provideZoneChangeDetection,
  ErrorHandler,
  APP_INITIALIZER,
  importProvidersFrom
} from '@angular/core';

import { environment } from './environments/environment';
import 'hammerjs';
import * as Sentry from '@sentry/angular';
import { NymblTheme } from '@app-layout/theme';
import {
  DatePipe,
  TitleCasePipe,
  LocationStrategy,
  PathLocationStrategy
} from '@angular/common';
import { DialogService } from 'primeng/dynamicdialog';
import { EntityIdPipe } from '@shared/pipes/entity/entity-id.pipe';
import { NoUnderscorePipe } from '@shared/pipes/no-underscore/no-underscore.pipe';
import { PersonsNamePipe } from '@shared/pipes/persons-name/persons-name.pipe';
import { StartCasePipe } from '@shared/pipes/start-case/start-case.pipe';
import { MenuService } from '@app-layout/navigation-menu';
import { BreadcrumbService } from '@app-layout/breadcrumbs';
import { CookieService } from 'ngx-cookie-service';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  CorsInterceptor,
  JwtInterceptor,
  TimezoneInterceptor,
  ErrorInterceptor
} from '@core/interceptors';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  RouteReuseStrategy,
  Router,
  provideRouter,
  withComponentInputBinding
} from '@angular/router';
import { RouterLogger } from '@core/routes/router-logger';
import { NymblRouteReuseStrategy } from '@core/routes/route-reuse-strategy';
import { appRoutes } from './app/app.routes';
import { providePrimeNG } from 'primeng/config';
import {
  PATIENT_SEARCH_NAVIGATION_SERVICE,
  RuntimeConfigService,
  StandalonePatientSearchNavigationService,
  StandaloneV1NavigationService,
  V1_NAVIGATION
} from '@core/services';
import { V1_ROOT_FALLBACK } from '@core/guards/v1-default/v1-root-fallback.token';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { DrawerModule } from 'primeng/drawer';
import { PanelMenuModule } from 'primeng/panelmenu';
import { SelectModule } from 'primeng/select';
import { PasswordModule } from 'primeng/password';
import { RadioButtonModule } from 'primeng/radiobutton';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { AppComponent } from './app/app.component';
import {
  provideTranslateLoader,
  provideTranslateService
} from '@ngx-translate/core';
import {
  AppTranslateLoader,
  DEFAULT_LANGUAGE
} from '@translations/app-translate-loader';

if (environment.production) {
  enableProdMode();
}

Sentry.init({
  environment: environment.production ? 'production' : 'development',
  dsn: environment.sentry.url,
  integrations: [
    // Registers and configures the Tracing integration,
    // which automatically instruments your application to monitor its
    // performance, including custom Angular routing instrumentation
    Sentry.browserTracingIntegration(),
    // Registers the Replay integration,
    // which automatically captures Session Replays
    Sentry.replayIntegration()
  ],

  tracesSampleRate: environment.sentry.traceRate,

  replaysSessionSampleRate: environment.sentry.replayRate,
  replaysOnErrorSampleRate: environment.sentry.replayOnErrorRate
});

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserAnimationsModule,
      BrowserModule,
      FormsModule,
      // Third party imports
      CardModule,
      DrawerModule,
      PanelMenuModule,
      SelectModule,
      PasswordModule,
      RadioButtonModule,
      CheckboxModule,
      ButtonModule,
      SkeletonModule,
      // Application imports
      ToastModule,
      InputTextModule,
      TooltipModule
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withComponentInputBinding()),
    { provide: RouteReuseStrategy, useClass: NymblRouteReuseStrategy },
    DatePipe,
    TitleCasePipe,
    EntityIdPipe,
    ConfirmationService,
    DialogService,
    NoUnderscorePipe,
    PersonsNamePipe,
    StartCasePipe,
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    MenuService,
    BreadcrumbService,
    CookieService,
    { provide: HTTP_INTERCEPTORS, useClass: CorsInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TimezoneInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    MessageService,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler()
    },
    { provide: Router, useClass: RouterLogger },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    providePrimeNG({
      theme: {
        preset: NymblTheme,
        options: {
          darkModeSelector: '[data-theme="dark"]',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng'
          }
        }
      }
    }),
    // TODO migrate to provideAppInitializer in v19
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true
    },
    RuntimeConfigService,
    // Swap to HybridPatientSearchNavigationService for the nymbl3 hybrid build, where
    // nymbl2 and the legacy AngularJS app share an origin — see patient-search-navigation.ts.
    {
      provide: PATIENT_SEARCH_NAVIGATION_SERVICE,
      useClass: StandalonePatientSearchNavigationService
    },
    // Every link into the legacy AngularJS app resolves through this one service. Swap to
    // HybridV1NavigationService for the nymbl3 hybrid build, for the same reason as the
    // patient search above — see v1-navigation.ts.
    {
      provide: V1_NAVIGATION,
      useClass: StandaloneV1NavigationService
    },
    // Plain `/` with no fragment redirects here -- there is no legacy app sharing this origin
    // to fall back to in a standalone deployment. See v1RootGuard and V1_ROOT_FALLBACK.
    { provide: V1_ROOT_FALLBACK, useValue: '/productivity' },
    provideTranslateService({
      lang: DEFAULT_LANGUAGE,
      fallbackLang: DEFAULT_LANGUAGE,
      loader: provideTranslateLoader(AppTranslateLoader)
    }),
    provideHttpClient(withInterceptorsFromDi())
  ]
}).catch((err) => console.error(err));
